import { isEdge, isMacOs } from 'react-device-detect';

// TypeScript's DOM lib has no SpeechRecognition (it is not a W3C standard yet), so the slice we use is typed here.
export type SpeechRecognitionResultLike = ArrayLike<{ transcript: string }> & { isFinal: boolean };
export type SpeechRecognitionEventLike = { results: ArrayLike<SpeechRecognitionResultLike>; resultIndex: number };
export type SpeechRecognitionErrorLike = { error: string };

export type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

// Set when the browser has the API but its service refuses (Edge's `network`, an unsupported language):
// trying again in the same session fails the same way, so the mic stays hidden.
let failedThisSession = false;

export const markSpeechUnsupportedForSession = () => {
  failedThisSession = true;
};

export const resetSpeechSupportForTests = () => {
  failedThisSession = false;
};

// Edge on macOS exposes the API, but its speech service refuses within a second of starting (`network` or
// `language-not-supported`), so the mic would flash and vanish. Hidden up front instead, like a browser without
// the API. Edge on Windows keeps the mic.
const isServiceKnownBroken = () => isEdge && isMacOs;

export const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | null => {
  if (failedThisSession || isServiceKnownBroken()) return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};
